import { Link, type Href } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, View } from "react-native";

import { Text } from "@/src/components/ui/BrandText";
import { ControlAccessGate } from "@/src/control/ControlAccessGate";
import { useControlAccessContext } from "@/src/control/ControlAccessContext";
import { ControlDisclosure, ControlId, ControlKeyValue } from "@/src/control/ControlDetails";
import { getControlDashboard, listIdentityRequests, type ControlDashboard } from "@/src/control/api";
import { hasControlPermission } from "@/src/control/permissions";
import { controlRoleLabel } from "@/src/control/presentation";
import { contentMaxWidths, contentPage } from "@/src/theme/layout";
import { border, radius, spacing, surface, text } from "@/src/theme/tokens";

export default function ControlDashboardScreen() {
  return <ControlAccessGate permission="control.dashboard.read"><Dashboard /></ControlAccessGate>;
}

function Dashboard() {
  const access = useControlAccessContext();
  const canReadIdentity = access.status === "allowed" && hasControlPermission(access.member, "identity.requests.read");
  const [data, setData] = useState<ControlDashboard | null>(null);
  const [pendingIdentityCount, setPendingIdentityCount] = useState<number | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);
  const load = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    let active = true;
    setError(null);
    getControlDashboard().then((value) => active && setData(value)).catch((cause: unknown) => {
      if (active) setError(cause instanceof Error ? cause : new Error(String(cause)));
    });

    if (!canReadIdentity) {
      setPendingIdentityCount(null);
    } else {
      listIdentityRequests("pending", 100)
        .then((value) => active && setPendingIdentityCount(value.length))
        .catch(() => active && setPendingIdentityCount(null));
    }

    return () => { active = false; };
  }, [attempt, canReadIdentity]);

  return (
    <ScrollView contentContainerStyle={styles.page}>
      <View style={styles.header}>
        <Text style={styles.kicker}>CONTROL CENTER</Text>
        <Text style={styles.title}>Vue d’ensemble</Text>
        <Text style={styles.subtitle}>Les priorités opérationnelles de Neqraw, au même endroit.</Text>
      </View>

      {error ? <ErrorCard onRetry={load} /> : !data ? <ActivityIndicator color={text.brand} /> : (
        <View style={styles.grid}>
          <Metric
            href={canReadIdentity ? "/control/identity" : undefined}
            label="Demandes d’identité en attente"
            value={pendingIdentityCount === null ? "—" : String(pendingIdentityCount)}
          />
          <Metric href="/control/moderation" label="Dossiers de modération ouverts" value={String(data.metrics.openModerationCases)} />
          <Metric href="/control/monetization" label="Campagnes actives" value={String(data.metrics.activeSponsoredCampaigns)} />
          <Metric href="/control/members" label="Membres Control actifs" value={String(data.metrics.activeControlMembers)} />
        </View>
      )}

      {access.status === "allowed" ? (
        <View style={styles.member}>
          <Text style={styles.cardTitle}>Votre accès</Text>
          <Text style={styles.memberName}>{access.member.displayName || access.member.email || "Membre Control"}</Text>
          {access.member.displayName && access.member.email ? <Text style={styles.body}>{access.member.email}</Text> : null}
          <Text style={styles.role}>{controlRoleLabel(access.member.role)}</Text>
          <ControlDisclosure>
            <ControlKeyValue label="Rôle technique" value={access.member.role} />
            <ControlId label="UID du membre" value={access.member.uid} />
            <ControlKeyValue label="Permissions accordées" value={access.member.permissions.join(" · ")} />
          </ControlDisclosure>
        </View>
      ) : null}
    </ScrollView>
  );
}

function Metric({ label, value, href }: { label: string; value: string; href?: string }) {
  const content = (
    <View style={styles.metricInner}>
      <Text style={styles.metricValue}>{value}</Text>
      <Text style={styles.metricLabel}>{label}</Text>
      {href ? <Text style={styles.metricLink}>Ouvrir la file →</Text> : null}
    </View>
  );
  if (!href) return <View style={styles.metric}>{content}</View>;
  return <Link href={href as Href} asChild><Pressable accessibilityRole="link" style={styles.metric}>{content}</Pressable></Link>;
}

function ErrorCard({ onRetry }: { onRetry: () => void }) {
  return (
    <View style={styles.card}>
      <Text style={styles.cardTitle}>Vue d’ensemble indisponible</Text>
      <Text style={styles.body}>L’accès est confirmé, mais les compteurs n’ont pas pu être chargés.</Text>
      <Pressable accessibilityRole="button" onPress={onRetry} style={styles.retry}><Text style={styles.link}>Réessayer</Text></Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  page: { ...contentPage(contentMaxWidths.control), gap: spacing.xl },
  header: { gap: spacing.sm },
  kicker: { color: text.brand, fontSize: 12, fontWeight: "800", letterSpacing: 1.5 },
  title: { color: text.primary, fontSize: 30, fontWeight: "800" },
  subtitle: { color: text.secondary, fontSize: 16, lineHeight: 24 },
  grid: { flexDirection: "row", flexWrap: "wrap", gap: spacing.md },
  metric: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.lg, borderWidth: 1, flexBasis: 220, flexGrow: 1, minWidth: 0 },
  metricInner: { gap: spacing.sm, minWidth: 0, padding: spacing.lg },
  metricValue: { color: text.brand, fontSize: 30, fontWeight: "800" },
  metricLabel: { color: text.primary, fontSize: 15, fontWeight: "700", lineHeight: 21 },
  metricLink: { color: text.brand, fontSize: 12, fontWeight: "800" },
  card: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.lg, borderWidth: 1, gap: spacing.sm, padding: spacing.lg },
  cardTitle: { color: text.primary, fontSize: 17, fontWeight: "800" },
  member: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.lg, borderWidth: 1, gap: spacing.md, padding: spacing.lg },
  memberName: { color: text.primary, fontSize: 16, fontWeight: "700" },
  body: { color: text.secondary, lineHeight: 22, minWidth: 0 },
  role: { alignSelf: "flex-start", backgroundColor: surface.selected, borderRadius: radius.pill, color: text.brand, fontWeight: "700", paddingHorizontal: spacing.md, paddingVertical: spacing.xs },
  retry: { alignSelf: "flex-start" },
  link: { color: text.brand, fontWeight: "800" },
});
